import { Color, Spacing, Typography } from '@persona-id/design-tokens';
import ChevronUpDownIcon from '@persona-id/design-tokens/icons/ChevronUpDownIcon';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import Dropdown from 'dashboard/components/Navbar/Dropdown';
import FooterImpersonation from 'dashboard/components/Navbar/FooterImpersonation';
import NavbarLink from 'dashboard/components/Navbar/NavbarLink';
import TruncatedText from 'dashboard/components/TruncatedText';
import AvailabilityCircle from 'dashboard/components-lib/AvailabilityCircle';
import { DashboardNavbarSetting, Organization } from 'dashboard/models/organization';
import { User, UserAvailability, UserResponseAttributes } from 'dashboard/models/user';
import { RootState } from 'dashboard/redux/store';
import { updateAvailability } from 'dashboard/redux/thunk-actions/current-user';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import IconUserProfilePicture from 'images/icons/user-profile-picture.svg';

const PROFILE_PICTURE_WIDTH = 32;
const PROFILE_PICTURE_HEIGHT = 32;

const ProfilePictureImage = styled('img')`
  border-radius: ${BORDER_RADIUSES.rounded};
  object-fit: cover;
`;

const StyledIconUserProfilePicture = styled(IconUserProfilePicture)`
  border-radius: ${BORDER_RADIUSES.rounded};
`;

interface FooterProps {
  isCollapsed: boolean;
  currentUser: User;
  organization: Organization;
  impersonationMode: string | undefined;
  expireSession: () => void | Promise<void>;
  toggleNavbarEdit: () => void;
  toggleNavbarCollapse: () => void;
}

const Footer = ({
  expireSession,
  isCollapsed,
  impersonationMode,
  currentUser,
  organization,
  toggleNavbarCollapse,
  toggleNavbarEdit,
}: FooterProps) => {
  const profilePictureUrl = useSelector(
    (state: RootState) =>
      (state.currentUser.currentUser.attributes as UserResponseAttributes)['profile-picture-url'],
  );
  const navigate = useNavigate();
  const hasRoleBasedNavbarSetting =
    organization.attributes['dashboard-navbar-setting'] === DashboardNavbarSetting.RoleBased;

  const userAvailabilityItems = Object.values(UserAvailability).map((availability) => (
    <div
      key={`set-${availability}`}
      id={`navbar__set-${availability}`}
      hidden={currentUser.attributes.availability === availability}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        updateAvailability(availability);
      }}
    >
      <NavbarLink
        label={
          <span>
            Set as&nbsp;<HighlightText>{availability}</HighlightText>
          </span>
        }
      />
    </div>
  ));

  return (
    <div>
      <Dropdown
        triggerOnHover={true}
        items={[
          ...userAvailabilityItems,
          !hasRoleBasedNavbarSetting && (
            <div
              key='configure-navbar'
              id='navbar__configure-navbar'
              onClick={async (e) => {
                e.preventDefault();
                toggleNavbarEdit();
                isCollapsed && toggleNavbarCollapse();
              }}
            >
              <NavbarLink label='Configure Sidebar' />
            </div>
          ),
          <NavbarLink key='profile' label='Profile' route='/dashboard/user/profile' />,
          <div
            key='log-out'
            id='navbar__log-out'
            onClick={async (e) => {
              e.preventDefault();
              await expireSession();
              navigate('/dashboard/login');
            }}
          >
            <NavbarLink label='Log out' variant='danger' />
          </div>,
        ]}
      >
        {({ openDropdown, closeDropdown }) => (
          <ProfileContainer
            id='navbar__profile'
            onClick={openDropdown}
            onMouseLeave={closeDropdown}
          >
            <UserProfileImageWrapper>
              {profilePictureUrl ? (
                <ProfilePictureImage
                  height={PROFILE_PICTURE_HEIGHT}
                  width={PROFILE_PICTURE_WIDTH}
                  src={profilePictureUrl}
                />
              ) : (
                <StyledIconUserProfilePicture
                  height={PROFILE_PICTURE_HEIGHT}
                  width={PROFILE_PICTURE_WIDTH}
                />
              )}
              <BackgroundCircle>
                {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                <StyledAvailabilityCircle availability={currentUser.attributes['availability']} />
              </BackgroundCircle>
            </UserProfileImageWrapper>
            <Spacer width={2} />

            <UserContent isCollapsed={isCollapsed}>
              <UserParagraph color='primary'>
                {currentUser.attributes['name-first']} {currentUser.attributes['name-last']}
              </UserParagraph>
              <UserParagraph size='sm' color='secondary'>
                {organization.attributes.name}
              </UserParagraph>
            </UserContent>

            <ChevronUpDownIcon />
            <Spacer width={1} height={0} />
          </ProfileContainer>
        )}
      </Dropdown>
      <FooterImpersonation impersonationMode={impersonationMode} />
    </div>
  );
};

export default Footer;

const BackgroundCircle = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.color.background.primary};
  position: absolute;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  right: -${Spacing[1]};
  bottom: ${Spacing[1]};
`;

const ProfileContainer = styled.div`
  display: flex;
  padding: ${Spacing[2]} ${Spacing[4]};
  align-items: center;
  user-select: none;

  border-top: 1px solid ${Color.BorderHairline};

  &:hover {
    background-color: ${({ theme }) => theme.color.primary.brand100};
    cursor: pointer;

    p {
      color: ${Color.TextActive};
    }
  }
  &:hover > div > ${BackgroundCircle} {
    background-color: ${({ theme }) => theme.color.primary.brand100};
  }
`;

const UserContent = styled.div<{ isCollapsed: boolean }>`
  display: flex;
  flex-direction: column;

  overflow: hidden;
  width: ${({ isCollapsed }) => (isCollapsed ? '0' : '100%')};
  transition: 0.5s;
`;

const UserParagraph = styled(Paragraph)`
  line-height: normal;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HighlightText = styled(TruncatedText)`
  ${Typography.TextMediumBold};
`;

const UserProfileImageWrapper = styled.div`
  flex: 1;
  position: relative;
`;

const StyledAvailabilityCircle = styled(AvailabilityCircle)`
  width: ${Spacing[2]};
  height: ${Spacing[2]};
  flex-shrink: 0;
`;
